import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point } from '@turf/helpers';
import { isNative } from '@/lib/native-auth';
import type { BundleTalhao } from './schemas';

// Captura de foto + GPS com plugins Capacitor no nativo e fallback web. Tudo
// funciona offline — nenhuma destas funções toca o servidor.

export interface FotoCapturada {
  blob: Blob;
  mime: string;
  filename: string;
}

export interface Coords {
  lat: number;
  lon: number;
  accuracy: number | null;
}

// ── Foto ───────────────────────────────────────────────────────────────────
export async function tirarFoto(): Promise<FotoCapturada | null> {
  if (isNative()) {
    try {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const photo = await Camera.getPhoto({
        quality: 70,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        correctOrientation: true,
        saveToGallery: false,
      });
      if (!photo.webPath) return null;
      const blob = await (await fetch(photo.webPath)).blob();
      const ext = photo.format || 'jpeg';
      return { blob, mime: blob.type || `image/${ext}`, filename: `foto.${ext}` };
    } catch {
      return null; // usuário cancelou a câmera
    }
  }
  // Web: input de arquivo com captura de câmera (mobile) / seleção (desktop).
  // Trata cancelamento (sem 'change') via 'cancel' + fallback no foco.
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.setAttribute('capture', 'environment');
    let done = false;
    const onFocus = () => window.setTimeout(() => finish(null), 500);
    function finish(v: FotoCapturada | null) {
      if (done) return;
      done = true;
      window.removeEventListener('focus', onFocus);
      resolve(v);
    }
    input.onchange = () => {
      const file = input.files?.[0];
      finish(file ? { blob: file, mime: file.type || 'image/jpeg', filename: file.name || 'foto.jpg' } : null);
    };
    input.oncancel = () => finish(null);
    window.addEventListener('focus', onFocus, { once: true });
    input.click();
  });
}

// ── GPS ──────────────────────────────────────────────────────────────────
export async function pegarLocalizacao(): Promise<Coords | null> {
  if (isNative()) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15000 });
      return { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy ?? null };
    } catch {
      return null;
    }
  }
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy ?? null }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  });
}

// ── Resolução do talhão por GPS (point-in-polygon, offline) ────────────────
export function resolverTalhao(talhoes: BundleTalhao[], lon: number, lat: number): BundleTalhao | null {
  const pt = point([lon, lat]);
  for (const t of talhoes) {
    const geo = t.geojson as GeoJSON.Geometry | null;
    if (!geo) continue;
    try {
      // booleanPointInPolygon aceita Polygon e MultiPolygon.
      if (booleanPointInPolygon(pt, geo as GeoJSON.Polygon | GeoJSON.MultiPolygon)) return t;
    } catch {
      /* geometria inválida — ignora */
    }
  }
  return null;
}

// ── Rede ───────────────────────────────────────────────────────────────────
export async function estaOnline(): Promise<boolean> {
  if (isNative()) {
    try {
      const { Network } = await import('@capacitor/network');
      return (await Network.getStatus()).connected;
    } catch {
      return typeof navigator !== 'undefined' ? navigator.onLine : true;
    }
  }
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

// Assina mudanças de conectividade. Devolve uma função de unsubscribe.
export function onConnectivityChange(cb: (online: boolean) => void): () => void {
  if (isNative()) {
    let remove: (() => void) | null = null;
    void import('@capacitor/network').then(({ Network }) => {
      Network.addListener('networkStatusChange', (s) => cb(s.connected)).then((h) => {
        remove = () => void h.remove();
      });
    });
    return () => {
      if (remove) remove();
    };
  }
  const on = () => cb(true);
  const off = () => cb(false);
  window.addEventListener('online', on);
  window.addEventListener('offline', off);
  return () => {
    window.removeEventListener('online', on);
    window.removeEventListener('offline', off);
  };
}
